/**
 * RecommendationCard — one Briefing AI recommendation (SYR-68, Phase 2).
 *
 * A `fix` (ranked FIX N) or `win` (QUICK WIN), with a title, rationale, and 1-2
 * objectives. Each objective shows its impact / effort and the element it
 * "moves" (resolved to the element's current BE score). An "Add to your Action
 * plan" foot commits the card (interim localStorage until Phase 4 persistence).
 */

import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { ScoreNode } from '../types';
import { ObjectiveChips } from './ObjectiveChips';
import { ObjectiveAskExpert } from './ObjectiveAskExpert';

export interface RecommendationObjective {
  task?: string;
  detail?: string;
  impact?: string;
  effort?: string;
  moves?: string;
}
export interface Recommendation {
  type?: 'fix' | 'win';
  title?: string;
  rationale?: string;
  objectives?: RecommendationObjective[];
}

export interface RecommendationCardProps {
  card: Recommendation;
  /** FIX number for fix cards (null for wins). */
  fixNo: number | null;
  added: boolean;
  onToggle: () => void;
  /** Delete this card from the set. Omitted for viewers / teaser cards. */
  onDismiss?: () => void;
  /** Resolve a `moves` element name → its ScoreNode (canonical or display). */
  resolve: (name: string) => ScoreNode | undefined;
}

const FIX = '#DC2626';
const WIN = '#16A34A';

export const RecommendationCard: React.FunctionComponent<RecommendationCardProps> = ({
  card,
  fixNo,
  added,
  onToggle,
  onDismiss,
  resolve,
}) => {
  const intl = useIntl();
  const isWin = card.type === 'win';
  const accent = isWin ? WIN : FIX;
  const objectives = card.objectives ?? [];

  // Two-step delete (mirrors the tab's Regenerate button): first click arms
  // the control, second click within 4s confirms. A deleted card can't be
  // individually restored, so a bare mis-click shouldn't remove it.
  const [deleteArmed, setDeleteArmed] = useState(false);
  const disarmTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (disarmTimer.current) window.clearTimeout(disarmTimer.current);
    },
    [],
  );
  const handleDismissClick = () => {
    if (!onDismiss) return;
    if (!deleteArmed) {
      setDeleteArmed(true);
      disarmTimer.current = window.setTimeout(() => setDeleteArmed(false), 4000);
      return;
    }
    if (disarmTimer.current) window.clearTimeout(disarmTimer.current);
    setDeleteArmed(false);
    onDismiss();
  };

  return (
    <div
      style={{
        background: '#fff',
        border: `2px solid ${accent}`,
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: 460,
        position: 'relative',
      }}
    >
      {onDismiss && (
        <button
          type="button"
          onClick={handleDismissClick}
          aria-label={intl.formatMessage(
            deleteArmed
              ? {
                  id: 'marketerView.recos.deleteConfirm',
                  defaultMessage: 'Delete?',
                }
              : {
                  id: 'marketerView.recos.deleteCard',
                  defaultMessage: 'Delete this recommendation',
                },
          )}
          title={intl.formatMessage({
            id: 'marketerView.recos.deleteCard',
            defaultMessage: 'Delete this recommendation',
          })}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            height: 26,
            width: deleteArmed ? 'auto' : 26,
            padding: deleteArmed ? '0 10px' : 0,
            borderRadius: 8,
            border: 'none',
            background: deleteArmed ? '#FEF2F2' : 'transparent',
            color: deleteArmed ? FIX : '#94A3B8',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            fontSize: deleteArmed ? 11.5 : 12,
            fontWeight: deleteArmed ? 700 : 400,
            whiteSpace: 'nowrap',
          }}
        >
          <CloseOutlined />
          {deleteArmed && (
            <FormattedMessage
              id="marketerView.recos.deleteConfirm"
              defaultMessage="Delete?"
            />
          )}
        </button>
      )}
      <div style={{ padding: '20px 22px 6px', flexShrink: 0 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            color: '#fff',
            padding: '4px 11px',
            borderRadius: 7,
            marginBottom: 14,
            background: accent,
          }}
        >
          {isWin ? (
            <FormattedMessage id="marketerView.recos.quickWin" defaultMessage="QUICK WIN" />
          ) : (
            <FormattedMessage
              id="marketerView.recos.fixN"
              defaultMessage="FIX {n}"
              values={{ n: fixNo ?? '' }}
            />
          )}
        </span>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', margin: '0 0 6px' }}>
          {card.title}
        </h3>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 22px 18px' }}>
        {card.rationale && (
          <p style={{ fontSize: 13.5, color: '#64748B', lineHeight: 1.5, margin: '0 0 16px' }}>
            {card.rationale}
          </p>
        )}
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#94A3B8',
            marginBottom: 10,
          }}
        >
          <FormattedMessage
            id="marketerView.recos.objectives"
            defaultMessage="Objectives ({n})"
            values={{ n: objectives.length }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {objectives.map((o, i) => (
            <div key={i} style={{ paddingLeft: 18, position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 2,
                  top: 7,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: accent,
                }}
              />
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', lineHeight: 1.4 }}>
                {o.task}
              </div>
              {o.detail && (
                <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.5, marginTop: 3 }}>
                  {o.detail}
                </div>
              )}
              <ObjectiveChips objective={o} resolve={resolve} />
              <ObjectiveAskExpert cardTitle={card.title} objective={o} resolve={resolve} />
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        style={{
          borderTop: '1px solid #F1F5F9',
          padding: '14px 22px',
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          flexShrink: 0,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          textAlign: 'left',
          width: '100%',
          border: 'none',
          background: added ? '#ECFDF5' : 'transparent',
          color: added ? '#047857' : '#64748B',
        }}
      >
        <span
          style={{
            width: 20,
            height: 20,
            borderRadius: 6,
            border: `1px solid ${added ? '#16A34A' : '#CBD5E1'}`,
            background: added ? '#16A34A' : '#fff',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          {added && <CheckOutlined />}
        </span>
        {added ? (
          <FormattedMessage
            id="marketerView.recos.added"
            defaultMessage="Added to your Action plan"
          />
        ) : (
          <FormattedMessage
            id="marketerView.recos.add"
            defaultMessage="Add to your Action plan"
          />
        )}
      </button>
    </div>
  );
};
